import PropTypes from 'prop-types';
import React from 'react';

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { Edit01, Copy01, Trash01, PauseCircle, PlayCircle } from '@opendate-app/setlist';

const axios = require('axios').default;
import { toast } from 'react-toastify';

import TicketTypeInventoryStats from '../../../shared/TicketTypeInventoryStats';
import { offsetDateForBrowser } from '../../../shared/timeZoneLogic';
import { priceRangeBounds, ticketTypeToPhases, dayOfBoundaryFor } from '../utils/ticketPhases';
import { pricePointTriggerLabel, quantityPricePoint } from '../utils/ticketPricePoints';
import { salesPauseActionLabel, toggleTicketTypeSalesPaused } from '../utils/ticketTypeSalesPause';

import { TicketTypeRowView, formatPrice, toAmount } from './TicketTypeRowView';
import './TicketTypeRowV2.css';

export { TicketTypeRowView, TicketTypeTableHeader } from './TicketTypeRowView';

const local = (value) => (value ? offsetDateForBrowser(value) : null);

const formatWindowDate = (date) => (date ? date.toLocaleString('en-US', {
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
}) : null);

const formatDay = (date) => (date ? date.toLocaleString('en-US', { month: 'short', day: 'numeric' }) : null);

const ONE_MINUTE = 60 * 1000;

const windowStatus = (start, end) => {
  const now = Date.now();
  if (start && new Date(start).getTime() > now) return `Scheduled · ${formatWindowDate(local(start))}`;
  if (end && new Date(end).getTime() <= now) return `Ended · ${formatWindowDate(local(end))}`;
  return start ? `On sale · ${formatWindowDate(local(start))}` : 'Unscheduled';
};

const sameTime = (a, b) => Boolean(a && b) && new Date(a).getTime() === new Date(b).getTime();

const presaleSub = (child, ticketType) => {
  const until = sameTime(child.end_time, ticketType.start_time) || !child.end_time
    ? 'public on-sale'
    : formatWindowDate(local(child.end_time));
  return child.access_code ? `Code ${child.access_code} · until ${until}` : `Until ${until}`;
};

export const salesWindowsFor = (ticketType, siblings, phases) => {
  const basePrice = ticketType.base_price_two_decimals ?? ticketType.base_price;
  const presaleChildren = siblings
    .filter((sibling) => sibling.presale_parent_id === ticketType.id)
    .map((child) => ({
      key: `child-${child.id}`,
      kind: 'presale',
      name: child.base_name,
      start: child.start_time,
      price: child.base_price_two_decimals ?? child.base_price,
      sub: presaleSub(child, ticketType),
      status: windowStatus(child.start_time, child.end_time),
    }));
  const presalePricePoints = phases.presale.pricePoints.map((pricePoint) => ({
    key: `price-point-${pricePoint.id || pricePoint.name}`,
    kind: 'presale',
    name: pricePoint.name,
    start: pricePoint.starts_at,
    price: pricePoint.price_two_decimals ?? pricePoint.price,
    sub: quantityPricePoint(pricePoint) ? 'Tickets-sold price' : 'Scheduled price',
    status: quantityPricePoint(pricePoint) ? pricePointTriggerLabel(pricePoint) : windowStatus(pricePoint.starts_at, null),
  }));
  const publicEnd = phases.dayOf.enabled && phases.dayOf.startsAt
    ? new Date(local(phases.dayOf.startsAt).getTime() - ONE_MINUTE)
    : local(ticketType.end_time);
  const waitsFor = ticketType.wait_for_ticket_type && ticketType.wait_for_ticket_type.id;

  return [
    ...[...presaleChildren, ...presalePricePoints].sort((a, b) => new Date(a.start || 0) - new Date(b.start || 0)),
    {
      key: 'public',
      kind: 'public',
      name: 'Public on-sale',
      start: ticketType.start_time,
      price: basePrice,
      sub: publicEnd ? `Everyone · until ${formatWindowDate(publicEnd)}` : 'Everyone',
      status: waitsFor
        ? `Starts after ${ticketType.wait_for_ticket_type.base_name}`
        : windowStatus(ticketType.start_time, ticketType.end_time),
    },
    ...(phases.dayOf.enabled ? [{
      key: 'day-of',
      kind: 'dayOf',
      name: phases.dayOf.name,
      start: phases.dayOf.startsAt,
      price: phases.dayOf.price != null ? phases.dayOf.price : basePrice,
      sub: 'Until event end',
      status: windowStatus(phases.dayOf.startsAt, ticketType.end_time),
    }] : []),
  ];
};

const inventoryText = (item) => (
  `${item.sold_count} sold · ${item.held_count} held · ${item.sellable_count} sellable`
);

const TicketTypeRowV2 = ({
  csrfToken,
  team,
  currentUser,
  confirm,
  fetchNewTicketType,
  ticketType,
  fetchTicketTypeForEdit,
  ticketTypeIsSeated,
  ticketGroup,
  parentTicketGroup,
  isLast,
  ticketGroupChanged,
  ticketTypesChanged,
  canAddSubGroup
}) => {
  const deleteTicketType = (csrfToken, team, ticketType) => {
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

    return axios.delete("/teams/" + team.id + "/ticket_types/" + ticketType.id)
      .then(({ data }) => {
        var updated = Object.assign({}, ticketGroup, {
          ticket_types: ticketGroup.ticket_types.filter((ticketType) => ticketType.id !== data.id)
        });

        ticketGroupChanged(updated);
      })
      .catch((error) => {
        toast.error(error.response.data.join(", "), {
          position: toast.POSITION.TOP_CENTER,
          draggable: false,
          closeOnClick: false,
          autoClose: 5000,
          hideProgressBar: true
        });
      });
  };

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({
    id: ticketType.id,
    data: {
      id: ticketType.id,
      ticketType: ticketType,
      ticketGroup: ticketGroup
    }
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  };

  const phases = ticketTypeToPhases(ticketType, dayOfBoundaryFor(confirm));
  const siblings = ticketGroup.ticket_types || [];
  const presaleWindowCount = siblings.filter((sibling) => sibling.presale_parent_id === ticketType.id).length;
  const presaleParent = siblings.find((sibling) => sibling.id === ticketType.presale_parent_id);
  const windows = ticketType.presale_parent_id ? [] : salesWindowsFor(ticketType, siblings, phases);
  const hasWindows = windows.length > 1;
  const basePrice = ticketType.base_price_two_decimals ?? ticketType.base_price;

  let subLine = ticketType.free
    ? 'Comp / free'
    : `${windows.length} sales window${windows.length === 1 ? '' : 's'}${hasWindows ? ' · 1 shared pool' : ''}`;

  if (ticketType.presale_parent_id) subLine = `Pre-sale of ${presaleParent ? presaleParent.base_name : 'its ticket'}`;
  if (ticketType.price_parent_id) subLine = ticketType.seating_chart_category_key ? 'Price option · shares its section\'s seats' : 'Price option · shares its ticket\'s capacity';

  const salesWindowText = (() => {
    if (ticketType.presale_parent_id || ticketType.wait_for_ticket_type?.id) return windows[0]?.status || windowStatus(ticketType.start_time, ticketType.end_time);
    const starts = windows.map((w) => w.start).filter(Boolean).sort((a, b) => new Date(a) - new Date(b));
    if (!starts.length || !ticketType.end_time) return 'Unscheduled';
    return `${formatDay(local(starts[0]))} → ${formatDay(local(ticketType.end_time))}`;
  })();

  const priceDisplay = (() => {
    if (ticketType.free) return 'FREE';
    const bounds = priceRangeBounds(ticketType);
    const prices = [
      ...(bounds ? [bounds.min, bounds.max] : []),
      ...windows.map((w) => toAmount(w.price)),
    ].filter((price) => !Number.isNaN(price));
    if (!prices.length) return formatPrice(basePrice);
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    return min === max ? formatPrice(min) : `${formatPrice(min)} – ${formatPrice(max)}`;
  })();

  const editTicketType = () => fetchTicketTypeForEdit(
    csrfToken, team,
    ticketType.presale_parent_id || ticketType.price_parent_id
      ? { ...ticketType, id: ticketType.presale_parent_id || ticketType.price_parent_id }
      : ticketType,
    ticketGroup,
  );

  const overflowActions = [
    {
      label: 'Edit',
      icon: Edit01,
      onClick: editTicketType,
    },
    ...(ticketType.presale_parent_id || ticketType.price_parent_id ? [] : [
      {
        label: salesPauseActionLabel(ticketType),
        icon: ticketType.sales_paused ? PlayCircle : PauseCircle,
        onClick: () => toggleTicketTypeSalesPaused(team, ticketType, ticketTypesChanged),
      },
    ]),
    ...(ticketTypeIsSeated(ticketType) || ticketType.presale_parent_id ? [] : [
      {
        label: 'Copy',
        icon: Copy01,
        onClick: () => fetchNewTicketType(
          csrfToken, team, confirm,
          ticketGroup.presale_pool && parentTicketGroup ? parentTicketGroup : ticketGroup,
          ticketType,
        ),
      },
      ...(presaleWindowCount > 0 ? [] : [{
        label: 'Delete',
        icon: Trash01,
        destructive: true,
        onClick: () => deleteTicketType(csrfToken, team, ticketType),
      }]),
    ]),
  ];

  const pool = parseInt(String(ticketType.available || 0).replace(/,/g, ''), 10) || 0;
  const soldPercent = pool > 0 ? Math.min(100, ((ticketType.sold_count || 0) / pool) * 100) : 0;

  return (
    <TicketTypeRowView
      sortableRef={setNodeRef}
      style={style}
      dragHandleProps={{ ...attributes, ...listeners }}
      last={!canAddSubGroup && isLast}
      name={ticketType.base_name}
      paused={Boolean(ticketType.sales_paused)}
      presale={Boolean(ticketType.presale_parent_id)}
      subLine={subLine}
      salesWindowText={salesWindowText}
      priceDisplay={priceDisplay}
      inventory={ticketType.sellable_count !== undefined ? (
        <span className="ticket-type-row-v2-inventory">{inventoryText(ticketType)}</span>
      ) : (
        <TicketTypeInventoryStats item={ticketType} className="ticket-type-row-v2-inventory" />
      )}
      actions={overflowActions}
      windows={windows}
      free={Boolean(ticketType.free)}
      basePrice={basePrice}
      poolCount={ticketType.available || 0}
      soldPercent={soldPercent}
      poolStats={ticketType.sellable_count !== undefined
        ? `${ticketType.sold_count} sold · ${ticketType.held_count} held · ${ticketType.reserved_count} reserved`
        : null}
      onEditWindow={editTicketType}
    />
  );
};

TicketTypeRowV2.propTypes = {
  csrfToken: PropTypes.string.isRequired,
  team: PropTypes.object.isRequired,
  currentUser: PropTypes.object.isRequired,
  confirm: PropTypes.object.isRequired,
  fetchNewTicketType: PropTypes.func.isRequired,
  ticketType: PropTypes.object.isRequired,
  fetchTicketTypeForEdit: PropTypes.func.isRequired,
  ticketTypeIsSeated: PropTypes.func.isRequired,
  ticketGroup: PropTypes.object.isRequired,
  parentTicketGroup: PropTypes.object,
  isLast: PropTypes.bool.isRequired,
  ticketGroupChanged: PropTypes.func.isRequired,
  ticketTypesChanged: PropTypes.func.isRequired,
  canAddSubGroup: PropTypes.bool.isRequired
};

export default TicketTypeRowV2;

